import { CloseOutlined } from '@ant-design/icons';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { CollapseProps } from 'antd/es/collapse';
import { default as Collapse } from 'antd/es/collapse';
import { default as Skeleton } from 'antd/es/skeleton';
import Tooltip from 'antd/es/tooltip';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fieldToDisplayableName } from 'utils/formatters/fieldToDisplayableName';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { dispatchGAEvent } from 'utils/googleAnalytics';
import { isSandboxEnvironment } from 'utils/sandbox/isSandboxEnvironment';

import { useGeneratePreviewURL } from '@pages/Preview';
import { SandboxPayoutDetailsTestCaseSection } from '@sandbox/SandboxPayoutDetailsTestCaseSection';
import type { PayoutRequestStatusTransitionsItem, ReasonCodeObject } from 'api';
import { useGetDisbursementPayoutRequestsUniqueReference } from 'api';
import { Restricted } from 'containers/Restricted';
import { canDownloadPaymentDocument, PaymentDocument } from 'pdf/payout';

import type { Metadata as ReasonCodeMetadata } from 'models/reasonCode';
import { ReasonCode } from 'models/reasonCode';

import { useCurrentOrganisation } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import { Badge } from '@atoms/badges/Badge/Badge';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { DetailsPayoutAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsPayoutAttachmentSectionContainer';
import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';
import { PDFIcon } from '@atoms/Icon/PDFIcon';
import { StatusTag } from '@atoms/StatusTag';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';
import { Country } from '@molecules/Country';
import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { ActivityDetails } from './ActivityDetails';
import { CancelPayout } from './CancelPayout';
import { PAYOUT_REQUEST_COLLAPSE_TEST_ID } from './constants';
import { countErrors } from './count-errors';
import { ErrorableDetailsItem } from './ErrorableDetailsItem';
import { ErrorBadge } from './ErrorBadge';
import { getAttributeErrors } from './get-attribute-errors';
import { getPayoutSummary } from './get-payout-summary';
import { PAYOUT_SUMMARIES_PER_STATUS } from './payout-summaries-per-status';
import { useIsPayoutCancellable } from './useIsPayoutCancellable';

import { PAYOUT_REQUEST_DETAILS_TEST_ID, PAYOUT_STATUSES } from '../constants';

const reasonCodeToSection: { [key: string]: PAYOUTS_KNOWN_SECTIONS } = {
  invalid_creditor: 'receiver',
  invalid_ultimate_debtor: 'sender',
  invalid_creditor_account: 'receiver_bank',
  invalid_general_data: 'payout_request',
  missing_capability: 'payout_request',
  waiting_future_execution: 'payout_request'
};

export type PAYOUTS_KNOWN_SECTIONS =
  | 'summary'
  | 'payout_request'
  | 'sender'
  | 'history'
  | 'receiver_bank'
  | 'receiver'
  | 'attachments'
  | 'intermediary_agent'
  | 'intermediary_agent_account'
  | 'sandbox';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

interface CollapseItemWithTypedSection extends Omit<CollapseItemType, 'key'> {
  key?: PAYOUTS_KNOWN_SECTIONS;
}

type Props = {
  uniqueReferenceId: string;
  onClose: VoidFunction;
  /**
   * Called when the status changes. Sandbox environment only.
   */
  onTestCaseSuccess?: VoidFunction;
};

export const PayoutDetails = ({
  uniqueReferenceId,
  onClose,
  onTestCaseSuccess
}: Props) => {
  const { isSandboxTestCasesEnabled } = useFeatureAccessControl();
  const organisation = useCurrentOrganisation();
  const {
    data: payoutDetailsData,
    isLoading,
    isError,
    isSuccess
  } = useGetDisbursementPayoutRequestsUniqueReference(
    organisation.id,
    uniqueReferenceId,
    { query: { select: (resp) => resp?.data } }
  );

  const reasonCodes: ReasonCode[] = useMemo(() => {
    return payoutDetailsData
      ? payoutDetailsData.reason_codes?.map((attrs: ReasonCodeObject) => {
          const meta: ReasonCodeMetadata = {
            requestedExecutionDate: payoutDetailsData.requested_execution_date
          };

          return new ReasonCode(attrs, payoutDetailsData.status, meta);
        })
      : [];
  }, [payoutDetailsData]);

  const paymentDocumentMetadata = useMemo(
    () => ({
      document: payoutDetailsData ? (
        <PaymentDocument
          organisation={organisation}
          payout={payoutDetailsData}
        />
      ) : undefined,
      filename: `Payment document - ${payoutDetailsData?.unique_reference}`,
      product: 'Payment document'
    }),
    [organisation, payoutDetailsData]
  );

  const documentPreviewURL = useGeneratePreviewURL(paymentDocumentMetadata);

  const isPayoutCancellable = useIsPayoutCancellable(payoutDetailsData);

  const hasReasonCodes = useMemo(() => {
    return (
      (reasonCodes?.length ?? 0) > 0 &&
      reasonCodes[0].symbol != 'requested_by_customer' &&
      reasonCodes[0].symbol != 'requested_by_merchant'
    );
  }, [reasonCodes]);

  const [activeSection, setActiveSection] =
    useState<PAYOUTS_KNOWN_SECTIONS>('payout_request');

  useEffect(() => {
    setActiveSection(hasReasonCodes ? 'summary' : 'payout_request');
  }, [hasReasonCodes]);

  const handleReasonCodeClick = useCallback((reasonCode: string) => {
    const section = reasonCodeToSection[reasonCode];
    if (section) setActiveSection(section);
  }, []);

  const collapseItems: CollapseItemWithTypedSection[] = useMemo(() => {
    if (!payoutDetailsData || !isSuccess) {
      return [];
    }

    const hasPayoutSummary =
      hasReasonCodes &&
      Object.keys(PAYOUT_SUMMARIES_PER_STATUS).includes(
        payoutDetailsData.status as unknown as string
      );

    const {
      errors,
      status,
      unique_reference: uniqueReference
    } = payoutDetailsData;

    const generalErrors = getAttributeErrors(
      errors,
      'missing_capability',
      'general'
    );

    const countedErrors = countErrors(errors);

    const sections: CollapseItemWithTypedSection[] = [];

    if (
      isSandboxEnvironment() &&
      isSandboxTestCasesEnabled &&
      onTestCaseSuccess
    ) {
      sections.push({
        key: 'sandbox',
        label: 'Sandbox',
        children: (
          <SandboxPayoutDetailsTestCaseSection
            currentStatus={status}
            uniqueReference={uniqueReference}
            onSuccess={onTestCaseSuccess}
          />
        )
      });
    }

    if (hasPayoutSummary) {
      sections.push({
        key: 'summary',
        label: 'Summary',
        children: (
          <div className="pb-2">
            <div className="mb-4">
              {hasReasonCodes && (
                <div className="flex flex-wrap gap-2">
                  {reasonCodes.map((reasonCode) => {
                    return (
                      <Badge
                        key={reasonCode.symbol}
                        label={reasonCode.label()}
                        className={cn(reasonCode.visualTheme(), {
                          'cursor-pointer':
                            reasonCode.hasWaitingForFutureExecutionSymbol()
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReasonCodeClick(reasonCode.symbol);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {getPayoutSummary(
              status,
              payoutDetailsData.reason_codes[0]?.symbol
            )}
          </div>
        )
      });
    }

    sections.push(
      {
        key: 'payout_request',
        label: (
          <div
            className="flex gap-2"
            data-testid={PAYOUT_REQUEST_COLLAPSE_TEST_ID}
          >
            Payout Request
            {countedErrors['payout_request'] > 0 ? (
              <span className="flex size-6 items-center justify-center rounded-full bg-feedback-error-surface text-sm font-medium text-white">
                {countedErrors['payout_request']}
              </span>
            ) : (
              ''
            )}
          </div>
        ),
        children: (
          <div>
            <ErrorBadge
              attributeErrors={generalErrors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
            />
            <ActivityDetails
              createdAt={payoutDetailsData.timestamp}
              createdBy={payoutDetailsData.creator_name}
              source={payoutDetailsData.source}
              batchId={payoutDetailsData.batch_id} // for legacy batch payouts with source=null
              cancellation={payoutDetailsData.cancellations[0]} // safe because we only have 1 cancellation per request
              reviews={payoutDetailsData.reviews}
            />
            <ErrorableDetailsItem
              title="Reference"
              item={
                <CopyToClipboardButton
                  text={payoutDetailsData.unique_reference?.toLocaleUpperCase()}
                />
              }
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="general_data"
              attrName="unique_reference"
              data-testid="payout-details-reference"
            />
            <ErrorableDetailsItem
              title="End-to-end ID"
              item={
                <CopyToClipboardButton
                  text={payoutDetailsData.end_to_end_id?.toLocaleUpperCase()}
                />
              }
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="general_data"
              attrName="end_to_end_id"
              data-testid="payout-details-e2e-id"
            />
            {payoutDetailsData.requested_execution_date && (
              <ErrorableDetailsItem
                title="Requested execution date"
                item={
                  <FormattedDate
                    value={payoutDetailsData.requested_execution_date}
                    className="text-base font-medium"
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="general_data"
                attrName="requested_execution_date"
                data-testid="payout-details-requested-execution-date"
              />
            )}
            <ErrorableDetailsItem
              title="Amount"
              item={
                <div>
                  <div>
                    {formatDecimalAmount(Number(payoutDetailsData.amount))}{' '}
                    {payoutDetailsData.currency}
                  </div>
                </div>
              }
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="general_data"
              attrName="amount"
              data-testid="payout-details-amount"
            />
            {payoutDetailsData.purpose_code && (
              <ErrorableDetailsItem
                title="Purpose Code"
                item={payoutDetailsData.purpose_code}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="general_data"
                attrName="purpose_code"
                data-testid="payout-details-purpose-code"
              />
            )}
            {payoutDetailsData.creditor_account?.country_code && (
              <ErrorableDetailsItem
                title="Country"
                item={
                  <Country
                    countryIso={
                      payoutDetailsData.creditor_account?.country_code
                    }
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="country_code"
                data-testid="payout-details-creditor-destination-country"
              />
            )}
            <ErrorableDetailsItem
              title="Transfer Type"
              item={payoutDetailsData.local_instrument}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="general_data"
              attrName="local_instrument"
              data-testid="payout-details-selected-product"
            />
            <ErrorableDetailsItem
              title="Business Unit"
              item={payoutDetailsData.business_unit}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="general_data"
              attrName="business_unit"
              data-testid="payout-details-business-unit"
            />
            {payoutDetailsData.debtor?.website && (
              <ErrorableDetailsItem
                title="Website"
                item={payoutDetailsData.debtor.website}
                data-testid="payout-details-debtor-website"
              />
            )}
            {canDownloadPaymentDocument(payoutDetailsData.status) && (
              <Restricted requiredPermission="exports:download">
                <hr />
                <div className="flex-column align-content-center flex w-full py-5">
                  <SecondaryButton
                    className="w-full"
                    size="large"
                    disabled={documentPreviewURL.valid === false}
                    onClick={() =>
                      dispatchGAEvent('pdf_document_generated', {
                        product: 'Payment document'
                      })
                    }
                    icon={<PDFIcon />}
                    href={documentPreviewURL.url}
                    target="_blank"
                  >
                    Payment document
                  </SecondaryButton>
                </div>
              </Restricted>
            )}
            {isPayoutCancellable && (
              <Restricted requiredPermission="payouts:manage">
                <hr />
                <CancelPayout payoutDetails={payoutDetailsData} />
              </Restricted>
            )}
          </div>
        )
      },
      {
        key: 'sender',
        label: 'Sender',
        children: (
          <div>
            <ErrorableDetailsItem
              title="Full Name"
              item={payoutDetailsData.ultimate_debtor?.full_name}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="full_name"
              data-testid="payout-details-debtor-full-name"
            />
            <ErrorableDetailsItem
              title="Name"
              item={(payoutDetailsData.ultimate_debtor as any)?.name}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="name"
              data-testid="payout-details-debtor-name"
            />
            <ErrorableDetailsItem
              title="Email"
              item={payoutDetailsData.ultimate_debtor?.email}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="email"
              data-testid="payout-details-debtor-email"
            />
            <ErrorableDetailsItem
              title="Mobile number"
              item={payoutDetailsData.ultimate_debtor?.mobile_number}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="mobile_number"
              data-testid="payout-details-debtor-mobile-number"
            />
            <ErrorableDetailsItem
              title="Date of Birth"
              item={
                <FormattedDate
                  value={payoutDetailsData.ultimate_debtor?.birthdate}
                  className="text-base font-medium"
                />
              }
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="birthdate"
              data-testid="payout-details-debtor-dob"
            />
            <ErrorableDetailsItem
              title="Address"
              item={payoutDetailsData.ultimate_debtor?.address}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="address"
              data-testid="payout-details-debtor-address"
            />
            <ErrorableDetailsItem
              title="Address lines"
              item={(payoutDetailsData.ultimate_debtor as any)?.address_lines}
              errors={errors}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              section="ultimate_debtor"
              attrName="address_lines"
              data-testid="payout-details-debtor-address-lines"
            />
            {(payoutDetailsData.ultimate_debtor as any)?.street_name && (
              <ErrorableDetailsItem
                title="Street name"
                item={(payoutDetailsData.ultimate_debtor as any)?.street_name}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="street_name"
                data-testid="payout-details-debtor-street-name"
              />
            )}
            {payoutDetailsData.ultimate_debtor?.postal_code && (
              <ErrorableDetailsItem
                title="Postal Code"
                item={payoutDetailsData.ultimate_debtor?.postal_code}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="postcode"
                data-testid="payout-details-debtor-postal-code"
              />
            )}
            {payoutDetailsData.ultimate_debtor?.city && (
              <ErrorableDetailsItem
                title="City"
                item={payoutDetailsData.ultimate_debtor?.city}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="city"
                data-testid="payout-details-debtor-city"
              />
            )}
            {payoutDetailsData.ultimate_debtor?.country_code && (
              <ErrorableDetailsItem
                title="Country"
                item={
                  <Country
                    countryIso={payoutDetailsData.ultimate_debtor?.country_code}
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="country_code"
                data-testid="payout-details-debtor-country"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)?.state && (
              <ErrorableDetailsItem
                title="State"
                item={(payoutDetailsData.ultimate_debtor as any)?.state}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="state"
                data-testid="payout-details-debtor-state"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)
              ?.country_of_birth_code && (
              <ErrorableDetailsItem
                title="Country of birth"
                item={
                  <Country
                    countryIso={
                      (payoutDetailsData.ultimate_debtor as any)
                        ?.country_of_birth_code
                    }
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="country_of_birth_code"
                data-testid="payout-details-debtor-country-of-birth"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)
              ?.country_of_residence_code && (
              <ErrorableDetailsItem
                title="Country of residence"
                item={
                  <Country
                    countryIso={
                      (payoutDetailsData.ultimate_debtor as any)
                        ?.country_of_residence_code
                    }
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="country_of_residence_code"
                data-testid="payout-details-debtor-country-of-residence"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)?.id_number && (
              <ErrorableDetailsItem
                title="ID number"
                item={(payoutDetailsData.ultimate_debtor as any)?.id_number}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="id_number"
                data-testid="payout-details-debtor-id-number"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)?.id_type && (
              <ErrorableDetailsItem
                title="ID type"
                item={(payoutDetailsData.ultimate_debtor as any)?.id_type}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="id_type"
                data-testid="payout-details-debtor-id-type"
              />
            )}
            {(payoutDetailsData.ultimate_debtor as any)?.type && (
              <ErrorableDetailsItem
                title="Type"
                item={(payoutDetailsData.ultimate_debtor as any)?.type}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="ultimate_debtor"
                attrName="type"
                data-testid="payout-details-debtor-type"
              />
            )}
          </div>
        )
      },
      {
        key: 'history',
        label: 'History',
        children: payoutDetailsData.status_transitions?.map(
          (transition: PayoutRequestStatusTransitionsItem, index: number) => (
            <div
              className="details-section-items-container"
              key={index}
              data-testid="payout-details-status-transition"
            >
              <div className="flex flex-col justify-between gap-1">
                <div className="text-xs">Status</div>
                <StatusTag
                  status={transition?.name}
                  statuses={PAYOUT_STATUSES}
                />
              </div>
              <div className="flex flex-col justify-end">
                <FormattedDateTime value={transition?.timestamp} />
              </div>
            </div>
          )
        )
      },
      {
        key: 'receiver_bank',
        label: 'Receiver Bank',
        children: (
          <div>
            {payoutDetailsData.creditor_account?.iban && (
              <ErrorableDetailsItem
                title="IBAN"
                item={
                  <CopyToClipboardButton
                    text={payoutDetailsData.creditor_account?.iban}
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="iban"
                data-testid="payout-details-creditor-receiver-bank"
              />
            )}
            {payoutDetailsData.creditor_account?.country_code && (
              <ErrorableDetailsItem
                title="Country"
                item={
                  <Country
                    countryIso={
                      payoutDetailsData.creditor_account?.country_code
                    }
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="country_code"
                data-testid="payout-details-creditor-country-code"
              />
            )}
            {payoutDetailsData.creditor_account?.account_number && (
              <ErrorableDetailsItem
                title="Account number"
                item={payoutDetailsData.creditor_account?.account_number}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="account_number"
                data-testid="payout-details-creditor-account-number"
              />
            )}
            {payoutDetailsData.creditor_account?.bank_clearing_system_id && (
              <ErrorableDetailsItem
                title="Clearing system ID"
                item={
                  payoutDetailsData.creditor_account?.bank_clearing_system_id
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="bank_clearing_system_id"
                data-testid="payout-details-creditor-clearing-system-id"
              />
            )}
            {payoutDetailsData.creditor_account?.bank_clearing_system_code && (
              <ErrorableDetailsItem
                title="Clearing system code"
                item={
                  payoutDetailsData.creditor_account?.bank_clearing_system_code
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="bank_clearing_system_code"
                data-testid="payout-details-creditor-clearing-system-code"
              />
            )}
            {payoutDetailsData.creditor_account?.bank_bic && (
              <ErrorableDetailsItem
                title="BIC"
                item={payoutDetailsData.creditor_account?.bank_bic}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="bank_bic"
                data-testid="payout-details-creditor-bic"
              />
            )}
            {payoutDetailsData.creditor_account?.bank_branch_id && (
              <ErrorableDetailsItem
                title="Branch ID"
                item={payoutDetailsData.creditor_account?.bank_branch_id}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="bank_branch_id"
                data-testid="payout-details-creditor-branch-id"
              />
            )}
            {payoutDetailsData.creditor_account?.bank_name && (
              <ErrorableDetailsItem
                title="Bank name"
                item={payoutDetailsData.creditor_account?.bank_name}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor_account"
                attrName="bank_name"
                data-testid="payout-details-creditor-bank-name"
              />
            )}
          </div>
        )
      },
      {
        key: 'receiver',
        label: 'Receiver',
        children: (
          <div>
            {payoutDetailsData.creditor?.full_name && (
              <ErrorableDetailsItem
                title="Full Name"
                item={payoutDetailsData.creditor?.full_name}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="full_name"
                data-testid="payout-details-creditor-full-name"
              />
            )}
            {(payoutDetailsData.creditor as any)?.name && (
              <ErrorableDetailsItem
                title="Name"
                item={(payoutDetailsData.creditor as any)?.name}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="name"
                data-testid="payout-details-creditor-name"
              />
            )}
            {payoutDetailsData.creditor?.email && (
              <ErrorableDetailsItem
                title="Email"
                item={payoutDetailsData.creditor?.email}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="email"
                data-testid="payout-details-creditor-email"
              />
            )}
            {payoutDetailsData.creditor?.birthdate && (
              <ErrorableDetailsItem
                title="Date of Birth"
                item={
                  <FormattedDate
                    value={payoutDetailsData.creditor?.birthdate}
                    className="font-semibold"
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="birthdate"
                data-testid="payout-details-creditor-dob"
              />
            )}
            {payoutDetailsData.creditor?.address && (
              <ErrorableDetailsItem
                title="Address"
                item={payoutDetailsData.creditor?.address}
                data-testid="payout-details-creditor-address"
              />
            )}
            {payoutDetailsData.creditor?.postal_code && (
              <ErrorableDetailsItem
                title="Postal Code"
                item={payoutDetailsData.creditor?.postal_code}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="postcode"
                data-testid="payout-details-creditor-postal-code"
              />
            )}
            {payoutDetailsData.creditor?.city && (
              <ErrorableDetailsItem
                title="City"
                item={payoutDetailsData.creditor?.city}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="city"
                data-testid="payout-details-creditor-city"
              />
            )}
            {payoutDetailsData.creditor?.country_code && (
              <ErrorableDetailsItem
                title="Country"
                item={
                  <Country
                    countryIso={payoutDetailsData.creditor?.country_code}
                  />
                }
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="country_code"
                data-testid="payout-details-creditor-country-code"
              />
            )}
            {(payoutDetailsData.creditor as any)?.state && (
              <ErrorableDetailsItem
                title="State"
                item={(payoutDetailsData.creditor as any)?.state}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="state"
                data-testid="payout-details-creditor-state"
              />
            )}
            {payoutDetailsData.creditor?.mobile_number && (
              <ErrorableDetailsItem
                title="Mobile number"
                item={payoutDetailsData.creditor?.mobile_number}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="mobile_number"
                data-testid="payout-details-creditor-mobile-number"
              />
            )}
            {payoutDetailsData.creditor?.vop_result && (
              <ErrorableDetailsItem
                title="VOP Result"
                item={fieldToDisplayableName(
                  payoutDetailsData.creditor?.vop_result
                )}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="vop_result"
                data-testid="payout-details-creditor-vop-result"
              />
            )}
            {payoutDetailsData.creditor?.vop_matched_name && (
              <ErrorableDetailsItem
                title="VOP Matched Name"
                item={payoutDetailsData.creditor?.vop_matched_name}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                section="creditor"
                attrName="vop_matched_name"
                data-testid="payout-details-creditor-vop-matched-name"
              />
            )}
          </div>
        )
      },
      {
        key: 'attachments',
        label: (
          <div className="flex items-center gap-1">
            Attachments
            <Tooltip
              title="This feature is not intended for RFIs"
              placement="right"
              arrow={true}
            >
              <InformationCircleIcon className="size-4 stroke-current" />
            </Tooltip>
          </div>
        ),
        children: (
          <DetailsPayoutAttachmentSectionContainer
            resourceId={payoutDetailsData.unique_reference}
          />
        )
      }
    );

    // Add Intermediary Agent info if required
    if (
      payoutDetailsData.intermediary_agent?.bank_bic &&
      payoutDetailsData.intermediary_agent?.bank_name &&
      payoutDetailsData.intermediary_agent_account?.account_number &&
      payoutDetailsData.intermediary_agent_account?.iban
    ) {
      sections.push(
        {
          key: 'intermediary_agent',
          label: 'Intermediary Agent',
          children: (
            <div>
              {payoutDetailsData.intermediary_agent?.bank_bic && (
                <ErrorableDetailsItem
                  title="Bank bic"
                  item={payoutDetailsData.intermediary_agent?.bank_bic}
                  errors={errors}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  section="intermediary_agent"
                  attrName="bank_bic"
                  data-testid="payout-details-intermediary-agent-bank-bic"
                />
              )}
              {payoutDetailsData.intermediary_agent?.bank_name && (
                <ErrorableDetailsItem
                  title="Bank name"
                  item={payoutDetailsData.intermediary_agent?.bank_name}
                  errors={errors}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  section="intermediary_agent"
                  attrName="bank_name"
                  data-testid="payout-details-intermediary-agent-bank-name"
                />
              )}
            </div>
          )
        },
        {
          key: 'intermediary_agent_account',
          label: 'Intermediary Agent Account',
          children: (
            <div>
              {payoutDetailsData.intermediary_agent_account?.account_number && (
                <ErrorableDetailsItem
                  title="Account number"
                  item={
                    payoutDetailsData.intermediary_agent_account?.account_number
                  }
                  errors={errors}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  section="intermediary_agent_account"
                  attrName="account_number"
                  data-testid="payout-details-intermediary-agent-account"
                />
              )}
              {payoutDetailsData.intermediary_agent_account?.iban && (
                <ErrorableDetailsItem
                  title="IBAN"
                  item={payoutDetailsData.intermediary_agent_account?.iban}
                  errors={errors}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  section="intermediary_agent_account"
                  attrName="iban"
                  data-testid="payout-details-intermediary-agent-account-iban"
                />
              )}
            </div>
          )
        }
      );
    }
    return sections;
  }, [
    payoutDetailsData,
    isSuccess,
    isSandboxTestCasesEnabled,
    onTestCaseSuccess,
    activeSection,
    documentPreviewURL.valid,
    documentPreviewURL.url,
    isPayoutCancellable,
    hasReasonCodes,
    reasonCodes,
    handleReasonCodeClick
  ]);

  return (
    <div
      data-testid={PAYOUT_REQUEST_DETAILS_TEST_ID}
      className="cool-shadow min-h-0 rounded-xl border"
    >
      <div className="flex justify-between rounded-t-xl bg-white px-6 py-3.5 text-xl">
        <span>Details</span>
        <TertiaryButton icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="bg-white">
        <div className="flex flex-col gap-3">
          {isLoading && <Skeleton className="p-6" />}
          {isSuccess && (
            <Collapse
              className="antd-details-collapse bg-white"
              expandIconPosition="end"
              accordion
              bordered={false}
              defaultActiveKey={activeSection}
              key={activeSection}
              items={collapseItems}
            />
          )}
          {isError && (
            <div className="flex items-center justify-center">
              Something went wrong
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
